import { allocate, decimal, isPositive } from '@aeris/money';
/**
 * Turning one agreed total into the instalments that will actually be due.
 *
 * "R$ 1.000,00 em 3x" is not three payments of R$ 333,33 — that is R$ 999,99,
 * and the missing centavo is the oldest bug in Brazilian commercial software.
 * `allocate` from @aeris/money distributes the total so the parts sum exactly
 * to the whole, and the leftover unit lands on the **first** instalment rather
 * than the last, which is the Brazilian convention: R$ 333,34 + R$ 333,33 +
 * R$ 333,33. A shop that collects the odd centavo up front is never short.
 */
/** Nobody agrees to 400 instalments; a typo in a quantity field does. */
export const MAX_INSTALMENTS = 360;
/**
 * Adds months while keeping the day of the month, clamped to the month's end.
 *
 * "Todo dia 31" in a 30-day month means the 30th, not the 1st of the next one.
 * JavaScript's `Date` rolls over silently, which is how 31 January + 1 month
 * becomes 3 March, so the day is clamped explicitly.
 */
const addMonths = (from, months) => {
    const day = from.getDate();
    const target = new Date(from.getTime());
    target.setDate(1);
    target.setMonth(target.getMonth() + months);
    const lastDay = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate();
    target.setDate(Math.min(day, lastDay));
    return target;
};
const addDays = (from, days) => new Date(from.getTime() + days * 86_400_000);
/**
 * The schedule for an agreement.
 *
 * Pure, and deliberately so: this is the arithmetic a screen wants to show
 * BEFORE anything is saved — "3x de R$ 333,34, 333,33, 333,33, vencendo 15/09,
 * 15/10, 15/11" — and a rule that only runs inside a transaction cannot be
 * previewed.
 */
export const scheduleInstalments = (input) => {
    if (!Number.isInteger(input.count) || input.count < 1) {
        return {
            ok: false,
            failure: { reason: 'count-out-of-range', count: input.count },
        };
    }
    if (input.count > MAX_INSTALMENTS) {
        return {
            ok: false,
            failure: { reason: 'count-out-of-range', count: input.count },
        };
    }
    if (!isPositive(input.total)) {
        return { ok: false, failure: { reason: 'total-not-positive' } };
    }
    let step;
    if (input.interval === 'days') {
        const days = input.intervalDays ?? 0;
        if (!Number.isInteger(days) || days < 1) {
            return { ok: false, failure: { reason: 'interval-days-not-positive' } };
        }
        step = (index) => addDays(input.firstDueAt, index * days);
    }
    else {
        step = (index) => addMonths(input.firstDueAt, index);
    }
    const one = decimal(1n, 0);
    const shares = allocate(input.total, Array.from({ length: input.count }, () => one));
    // A total that cannot be split without producing an empty instalment is an
    // agreement nobody meant to make: two centavos in three parts is one part of
    // nothing, and a title of R$ 0,00 is a row claiming a debt that does not
    // exist. Refused by name rather than silently dropped, because the caller
    // asked for three and getting two back quietly would be worse.
    if (shares.some((share) => !isPositive(share))) {
        return { ok: false, failure: { reason: 'instalment-would-be-zero' } };
    }
    return {
        ok: true,
        instalments: shares.map((amount, index) => ({
            number: index + 1,
            dueAt: step(index),
            amount,
        })),
    };
};
//# sourceMappingURL=schedule.js.map